import React, { useEffect, useMemo, useState } from 'react';
import EventStudyChart from './EventStudyChart';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { fetchActivistProfile } from '../config/api';
import SeoHead from '../seo/SeoHead';
import { activistMeta } from '../seo/metadata';
import { getActivistDescription } from '../seo/activistDescriptions';
import { personProfileSchema, breadcrumbSchema } from '../seo/structuredData';
import { trackProfileView } from '../seo/analytics';
import { cleanTitle } from '../utils/reportTitle';

const INTER_STACK = "'Inter', system-ui, -apple-system, sans-serif";

const headingStyle = {
  fontFamily: INTER_STACK,
  fontWeight: 200,
  letterSpacing: '-0.02em',
};

const bodyStyle = {
  fontFamily: INTER_STACK,
  fontWeight: 300,
};

const labelStyle = {
  ...bodyStyle,
  fontSize: '0.65rem',
  letterSpacing: '0.22em',
};

const ctaStyle = {
  fontFamily: INTER_STACK,
  fontWeight: 500,
  letterSpacing: '0.18em',
  fontSize: '0.7rem',
};

const parseCount = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};


const sectionHeadStyle = { fontFamily: INTER_STACK, fontWeight: 300, fontSize: 'clamp(1.1rem, 2vw, 1.35rem)', letterSpacing: '-0.01em' };

// A summary metric tile.
const StatTile = ({ value, label }) => (
  <div className="bg-gray-50 rounded-lg px-4 py-3">
    <div className="text-gray-900" style={{ fontFamily: INTER_STACK, fontWeight: 300, fontSize: '1.4rem', letterSpacing: '-0.02em' }}>{value}</div>
    <div className="text-gray-500 mt-0.5" style={{ ...bodyStyle, fontSize: '0.78rem' }}>{label}</div>
  </div>
);

// Post-report return, coloured from the stock's perspective (down = red).
const ReturnPct = ({ value }) => {
  if (value === null || value === undefined || value === '') return <span className="text-gray-300" style={{ fontSize: '0.8rem' }}>—</span>;
  const n = Number(value);
  if (!Number.isFinite(n)) return <span className="text-gray-300" style={{ fontSize: '0.8rem' }}>—</span>;
  const down = n < 0;
  return (
    <span style={{ fontFamily: INTER_STACK, fontWeight: 500, fontSize: '0.82rem', color: down ? '#b91c1c' : '#15803d' }}>
      {down ? '' : '+'}{Math.round(n)}%
    </span>
  );
};

// Horizontal proportional-bar breakdown (sectors / regions / exchanges).
const BreakdownBars = ({ items, labelKey, max, columns = 1 }) => {
  const top = max || Math.max(1, ...items.map((i) => parseCount(i.attack_count)));
  const wrapCls = columns === 2
    ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2.5'
    : 'space-y-2.5';
  return (
    <div className={wrapCls}>
      {items.map((item) => {
        const count = parseCount(item.attack_count);
        return (
          <div key={item[labelKey]}>
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-gray-600" style={{ ...bodyStyle, fontSize: '0.82rem' }}>{item[labelKey]}</span>
              <span className="text-gray-900" style={{ fontFamily: INTER_STACK, fontWeight: 500, fontSize: '0.8rem' }}>{count}</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full bg-gray-900 transition-all duration-700" style={{ width: `${(count / top) * 100}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// One "Focus" panel with a Sector / Region / Exchange toggle.
const FocusBreakdown = ({ sectors, regions, exchanges }) => {
  const tabs = [
    { key: 'sector', label: 'Sector', items: sectors || [], labelKey: 'sector' },
    { key: 'region', label: 'Region', items: regions || [], labelKey: 'region' },
    { key: 'exchange', label: 'Exchange', items: exchanges || [], labelKey: 'exchange' },
  ].filter((t) => t.items.length > 0);
  const [active, setActive] = useState(tabs[0] ? tabs[0].key : 'sector');
  if (tabs.length === 0) return null;
  const current = tabs.find((t) => t.key === active) || tabs[0];
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h2 className="text-gray-900" style={sectionHeadStyle}>Focus</h2>
        <div className="inline-flex rounded-lg border border-gray-200 p-0.5">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`px-3.5 py-1 rounded-md transition-colors ${active === t.key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}
              style={{ ...bodyStyle, fontSize: '0.8rem' }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <BreakdownBars items={current.items.slice(0, 12)} labelKey={current.labelKey} columns={2} />
    </div>
  );
};

// Sortable / filterable / paginated campaign history table.
const PAGE_SIZE = 15;
const CampaignTable = ({ reports, activistName, totalReports }) => {
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [page, setPage] = useState(0);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q
      ? reports.filter((r) =>
          (r.target_company || '').toLowerCase().includes(q) ||
          (r.ticker || '').toLowerCase().includes(q))
      : reports.slice();
    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sort.key === 'return') {
        const av = a.total_return_pct == null || a.total_return_pct === '' ? null : Number(a.total_return_pct);
        const bv = b.total_return_pct == null || b.total_return_pct === '' ? null : Number(b.total_return_pct);
        if (av == null && bv == null) return 0;
        if (av == null) return 1; // nulls always last
        if (bv == null) return -1;
        return (av - bv) * dir;
      }
      if (sort.key === 'company') {
        return (a.target_company || '').localeCompare(b.target_company || '') * dir;
      }
      const av = new Date(a.publication_date).getTime() || 0;
      const bv = new Date(b.publication_date).getTime() || 0;
      return (av - bv) * dir;
    });
    return list;
  }, [reports, filter, sort]);

  useEffect(() => { setPage(0); }, [filter, sort]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const start = page * PAGE_SIZE;
  const paged = rows.slice(start, start + PAGE_SIZE);

  const toggle = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'company' ? 'asc' : 'desc' }));
  const arrow = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');
  const thBase = 'px-3 py-2.5 text-gray-500 whitespace-nowrap';
  const thStyle = { ...bodyStyle, fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="text-gray-900" style={sectionHeadStyle}>Campaign history</h2>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by company or ticker"
          className="px-3 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:border-gray-400"
          style={{ ...bodyStyle, fontSize: '0.82rem', minWidth: '220px' }}
        />
      </div>

      <div className="overflow-x-auto border border-gray-100 rounded-lg">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className={`${thBase} text-left cursor-pointer select-none`} style={thStyle} onClick={() => toggle('date')}>Date{arrow('date')}</th>
              <th className={`${thBase} text-left cursor-pointer select-none`} style={thStyle} onClick={() => toggle('company')}>Company{arrow('company')}</th>
              <th className={`${thBase} text-left`} style={thStyle}>Ticker</th>
              <th className={`${thBase} text-left`} style={thStyle}>Report</th>
              <th className={`${thBase} text-left`} style={thStyle}>Sector</th>
              <th className={`${thBase} text-right cursor-pointer select-none`} style={thStyle} onClick={() => toggle('return')}>Return{arrow('return')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {paged.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400" style={{ ...bodyStyle, fontSize: '0.85rem' }}>No matching reports</td></tr>
            ) : paged.map((r, i) => {
              const title = cleanTitle(r.report_title);
              const showTitle = title && title.toLowerCase() !== (r.target_company || '').toLowerCase();
              return (
                <tr key={`${r.link}-${start + i}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap" style={{ ...bodyStyle, fontSize: '0.8rem' }}>{fmtDate(r.publication_date)}</td>
                  <td className="px-3 py-2.5 text-gray-800" style={{ ...bodyStyle, fontSize: '0.85rem' }}>{r.target_company || '—'}</td>
                  <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap" style={{ fontFamily: INTER_STACK, fontSize: '0.8rem' }}>{r.ticker || '—'}</td>
                  <td className="px-3 py-2.5" style={{ maxWidth: '22rem' }}>
                    {showTitle ? (
                      r.link ? (
                        <a href={r.link} target="_blank" rel="noopener noreferrer" className="block truncate text-gray-600 hover:text-gray-900 hover:underline" style={{ ...bodyStyle, fontSize: '0.8rem' }} title={title}>{title}</a>
                      ) : (
                        <span className="block truncate text-gray-600" style={{ ...bodyStyle, fontSize: '0.8rem' }} title={title}>{title}</span>
                      )
                    ) : (
                      r.link ? (
                        <a href={r.link} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600" style={{ ...bodyStyle, fontSize: '0.8rem' }}>View →</a>
                      ) : <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500" style={{ ...bodyStyle, fontSize: '0.8rem' }}>{r.sector || '—'}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap"><ReturnPct value={r.total_return_pct} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
        <span className="text-gray-400" style={{ ...bodyStyle, fontSize: '0.78rem' }}>
          {rows.length === 0 ? '0 reports' : `${start + 1}–${Math.min(start + PAGE_SIZE, rows.length)} of ${rows.length.toLocaleString()}${filter ? ` (filtered from ${totalReports.toLocaleString()})` : ''}`}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
            className="px-3 py-1.5 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white transition-colors" style={{ ...bodyStyle, fontSize: '0.8rem' }}>← Prev</button>
          <span className="px-3 text-gray-500" style={{ ...bodyStyle, fontSize: '0.8rem' }}>Page {page + 1} of {pageCount}</span>
          <button onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
            className="px-3 py-1.5 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white transition-colors" style={{ ...bodyStyle, fontSize: '0.8rem' }}>Next →</button>
        </div>
      </div>
    </div>
  );
};

// Reports-per-year timeline (historical coverage).
const YearChart = ({ years }) => {
  const data = [...years].sort((a, b) => parseCount(a.year) - parseCount(b.year));
  const max = Math.max(1, ...data.map((y) => parseCount(y.attack_count)));
  return (
    <div className="flex items-end gap-1.5 sm:gap-2" style={{ height: 150 }}>
      {data.map((y) => {
        const v = parseCount(y.attack_count);
        return (
          <div key={y.year} className="flex-1 flex flex-col items-center justify-end gap-1.5" title={`${parseCount(y.year)}: ${v} reports`}>
            <span className="text-gray-500" style={{ fontFamily: INTER_STACK, fontWeight: 500, fontSize: '0.68rem' }}>{v}</span>
            <div className="w-full rounded-t bg-gray-900 hover:bg-gray-700 transition-colors" style={{ height: `${Math.max(2, (v / max) * 120)}px` }} />
            <span className="text-gray-400" style={{ ...bodyStyle, fontSize: '0.68rem' }}>{String(parseCount(y.year)).slice(2)}</span>
          </div>
        );
      })}
    </div>
  );
};

const PublicHeader = () => (
  <header className="absolute top-0 left-0 right-0 z-30 px-6">
    <div className="max-w-6xl mx-auto py-5 sm:py-7">
      <nav className="flex items-center justify-between gap-4">
        <Link
          to="/"
          className="text-white leading-none hover:text-white/80 transition-colors"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontWeight: 400,
            letterSpacing: '-0.01em',
            fontSize: 'clamp(1.5rem, 3.6vw, 2.75rem)',
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
          }}
        >
          Phalanx
        </Link>
        <div
          className="flex items-center gap-4 sm:gap-6"
          style={{
            ...bodyStyle,
            fontSize: '0.85rem',
            letterSpacing: '-0.01em',
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
          }}
        >
          <Link to="/research" className="text-white/70 hover:text-white transition-colors">
            Research
          </Link>
          <Link to="/login" className="text-white/70 hover:text-white transition-colors">
            Login
          </Link>
        </div>
      </nav>
    </div>
  </header>
);

const SubscriberCta = () => (
  <div className="border border-gray-200 rounded-xl p-5 sm:p-6 bg-gray-50">
    <p className="text-gray-600 leading-relaxed mb-5" style={{ ...bodyStyle, fontSize: '0.95rem' }}>
      Full report archive and campaign analysis are available to Phalanx subscribers.
    </p>
    <Link
      to="/login"
      className="inline-flex items-center gap-2 text-gray-900 border-b border-gray-900 hover:border-gray-500 hover:text-gray-600 pb-0.5 uppercase transition-colors"
      style={ctaStyle}
    >
      Access short seller data
      <span>→</span>
    </Link>
  </div>
);

const DetailedActivistProfile = ({ activistName, onBack, isPublicView = false }) => {
  const [loading, setLoading] = useState(true);
  const [activistApiData, setActivistApiData] = useState(null);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setApiError(null);
        const data = await fetchActivistProfile(activistName);
        if (!cancelled) setActivistApiData(data);
      } catch (err) {
        if (!cancelled) setApiError('Unable to load this activist profile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [activistName]);

  const summary = activistApiData?.summary || {};
  const sectorBreakdown = activistApiData?.sectorBreakdown || [];
  const regionBreakdown = activistApiData?.regionBreakdown || [];
  const exchangeBreakdown = activistApiData?.exchangeBreakdown || [];
  const yearlyActivity = activistApiData?.yearlyActivity || [];
  const reports = activistApiData?.reports || activistApiData?.recentReports || [];
  const performance = activistApiData?.performance || {};
  const totalReports = parseCount(summary.total_reports);
  const companiesTargeted = parseCount(summary.companies_targeted);
  const sectorsTargeted = parseCount(summary.sectors_targeted);
  const firstYear = summary.first_attack ? new Date(summary.first_attack).getFullYear() : null;
  const latestYear = summary.latest_attack ? new Date(summary.latest_attack).getFullYear() : null;
  const activeSpan = firstYear && latestYear ? `${latestYear - firstYear + 1} yrs` : '—';
  const description = getActivistDescription(activistName);

  const seo = activistMeta({
    name: activistName,
    totalReports,
    companiesTargeted,
    latestAttack: summary.latest_attack,
  });

  useEffect(() => {
    if (loading || !activistApiData) return;
    trackProfileView({ activistName, totalReports });
  }, [loading, activistApiData, activistName, totalReports]);

  const profileSeoHead = (
    <SeoHead
      {...seo}
      jsonLd={[
        personProfileSchema({
          name: activistName,
          totalReports,
          companiesTargeted,
        }),
        breadcrumbSchema(
          isPublicView
            ? [
                { name: 'Phalanx', path: '/' },
                { name: activistName, path: `/activist/${encodeURIComponent(activistName)}` },
              ]
            : [
                { name: 'Phalanx', path: '/' },
                { name: 'Activists', path: '/activists' },
                { name: activistName, path: `/activist/${encodeURIComponent(activistName)}` },
              ]
        ),
      ]}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" style={{ fontFamily: INTER_STACK }}>
        {profileSeoHead}
        <p className="text-gray-500" style={bodyStyle}>Loading profile…</p>
      </div>
    );
  }

  if (apiError || !activistApiData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6" style={{ fontFamily: INTER_STACK }}>
        {profileSeoHead}
        <p className="text-gray-600 mb-6" style={bodyStyle}>{apiError || 'Profile not found.'}</p>
        <button
          onClick={onBack}
          className="text-gray-900 border-b border-gray-900 hover:text-gray-600 hover:border-gray-600 uppercase transition-colors"
          style={ctaStyle}
        >
          Go back
        </button>
      </div>
    );
  }

  const content = (
    <>
      {profileSeoHead}

      {!isPublicView && (
        <div className="max-w-3xl mx-auto px-6 pt-8">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-6"
            style={{ ...bodyStyle, fontSize: '0.85rem' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      )}

      {isPublicView && (
        <section className="relative h-[44vh] min-h-[300px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-bottom bg-no-repeat"
            style={{ backgroundImage: "url('/phalanx-bg.png')", backgroundSize: 'cover' }}
          />
          <div className="absolute inset-0 bg-black/55" />
          <PublicHeader />
          <div className="relative h-full flex items-center justify-center px-6 pt-16">
            <div className="text-center max-w-3xl">
              <p
                className="text-white/60 mb-4 uppercase"
                style={{ ...labelStyle, fontSize: '0.68rem', letterSpacing: '0.28em' }}
              >
                Activist short seller
              </p>
              <h1
                className="text-white leading-[1.15]"
                style={{
                  ...headingStyle,
                  fontSize: 'clamp(1.85rem, 4.4vw, 3rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)',
                }}
              >
                {activistName}
              </h1>
            </div>
          </div>
        </section>
      )}

      {!isPublicView && (
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-3xl mx-auto px-6 py-10">
            <p className="text-gray-400 mb-3 uppercase" style={{ ...labelStyle, fontSize: '0.68rem' }}>
              Activist short seller
            </p>
            <h1
              className="text-gray-900 leading-[1.15]"
              style={{ ...headingStyle, fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}
            >
              {activistName}
            </h1>
          </div>
        </section>
      )}

      <section className="py-12 sm:py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto space-y-12">
          {description && (
            <div className="space-y-5">
              {description.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '0.98rem' }}>
                  {paragraph.trim()}
                </p>
              ))}
            </div>
          )}

          {/* Key stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatTile value={totalReports.toLocaleString()} label="Reports" />
            <StatTile value={companiesTargeted.toLocaleString()} label="Companies" />
            <StatTile value={sectorsTargeted} label="Sectors" />
            <StatTile value={activeSpan} label={firstYear ? `Since ${firstYear}` : 'Active span'} />
            {parseCount(performance.reports_with_return) > 0 && (
              <>
                <StatTile value={`${Math.round(Number(performance.median_total_return_pct))}%`} label="Median return" />
                <StatTile value={`${Math.round(Number(performance.pct_declined))}%`} label="Targets declined" />
              </>
            )}
          </div>
          {parseCount(performance.reports_with_return) > 0 && (
            <p className="text-gray-400 -mt-8" style={{ ...bodyStyle, fontSize: '0.76rem' }}>
              Return measured from each report date to {performance.price_as_of ? fmtDate(performance.price_as_of) : 'the last price update'}
              {' · '}{parseCount(performance.reports_with_return)} of {totalReports} reports priced
            </p>
          )}

          {/* Historical coverage */}
          {yearlyActivity.length > 0 && (
            <div>
              <h2 className="text-gray-900 mb-1" style={sectionHeadStyle}>Historical coverage</h2>
              <p className="text-gray-500 mb-5" style={{ ...bodyStyle, fontSize: '0.82rem' }}>Reports published per year</p>
              <YearChart years={yearlyActivity} />
            </div>
          )}

          {/* Price impact — the median path across this firm's targets, against
              the all-sellers baseline. Gated to the private view: the numbers
              are still thin (price coverage is 2026-targeted US names only) and
              a seller-level track record should not be public until it is
              properly covered. */}
          {!isPublicView && (
            <div>
              <EventStudyChart seller={activistName} />
            </div>
          )}

          {/* Focus — sector / region / exchange toggle */}
          <FocusBreakdown sectors={sectorBreakdown} regions={regionBreakdown} exchanges={exchangeBreakdown} />

          {/* Campaign history */}
          {isPublicView ? (
            <SubscriberCta />
          ) : (
            reports.length > 0 && (
              <CampaignTable reports={reports} activistName={activistName} totalReports={totalReports} />
            )
          )}
        </div>
      </section>

      {isPublicView && (
        <section className="py-10 px-6 border-t border-gray-100 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors"
              style={{ ...bodyStyle, fontSize: '0.85rem' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Phalanx
            </button>
          </div>
        </section>
      )}
    </>
  );

  if (isPublicView) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: INTER_STACK }}>
        {content}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12" style={{ fontFamily: INTER_STACK }}>
      {content}
    </div>
  );
};

export default DetailedActivistProfile;
